// Overall ranking across categories, weighted by the user's spending mix.
//
// Input is data/overall-rates.json (built by scripts/precompute_category_rankings.mjs):
// for every category + city scope, each card's
//   savingRate = avg saving at a covered merchant / that category's default bill
//   coverage   = share of the category's merchants (in scope) the card covers
// The model is an expected value per category:
//   yearly saving = Σ_c  monthlySpend_c × 12 × savingRate_c × coverage_c
// "Of every rupee you spend on Health, this card takes savingRate off at the
// coverage share of places that take it." Net = yearly saving − annual fee.
//
// Pure + dependency-free so the browser (assets/algorithms.js) and mobile
// (src/lib/overall.ts mirrors it) compute identical numbers.

/**
 * @param {object} args
 * @param {{cards: Array<[string,string,string]>, rates: Record<string, Record<string, Array<[number,number,number]>>>}} args.rates
 * @param {Record<string, number>} args.monthlySpend  PKR per month by category slug (missing/0 = excluded)
 * @param {string} [args.scope]  "all" | "karachi" | "lahore" | "islamabad"
 * @param {(bank: string, card: string) => number|null} [args.annualFee]
 * @param {(bank: string, card: string, cardType: string) => boolean} [args.include]  extra filter (banks, card types, eligibility)
 * @returns {Array<{bank: string, card: string, cardCategory: string, yearlySaving: number, annualFee: number|null, net: number, byCategory: Array<{slug: string, yearlySaving: number, savingRate: number, coverage: number}>}>}
 */
export function computeOverallRanking({ rates, monthlySpend, scope = "all", annualFee, include }) {
  if (!rates || !Array.isArray(rates.cards) || !rates.rates) return [];
  const scopeRates = rates.rates[scope] || rates.rates.all || {};
  const totals = new Map();
  for (const [slug, rows] of Object.entries(scopeRates)) {
    const spend = Number(monthlySpend?.[slug]);
    if (!Number.isFinite(spend) || spend <= 0 || !Array.isArray(rows)) continue;
    for (const [idx, savingRate, coverage] of rows) {
      const yearly = spend * 12 * (Number(savingRate) || 0) * (Number(coverage) || 0);
      if (!(yearly > 0)) continue;
      let entry = totals.get(idx);
      if (!entry) { entry = { yearlySaving: 0, byCategory: [] }; totals.set(idx, entry); }
      entry.yearlySaving += yearly;
      entry.byCategory.push({ slug, yearlySaving: yearly, savingRate, coverage });
    }
  }
  const out = [];
  for (const [idx, entry] of totals) {
    const [bank, card, cardCategory] = rates.cards[idx] || [];
    if (!bank || !card) continue;
    if (include && !include(bank, card, cardCategory)) continue;
    const fee = annualFee ? annualFee(bank, card) : null;
    const feeNum = Number.isFinite(fee) ? fee : null;
    entry.byCategory.sort((a, b) => b.yearlySaving - a.yearlySaving);
    out.push({
      bank,
      card,
      cardCategory: cardCategory || "other",
      yearlySaving: entry.yearlySaving,
      annualFee: feeNum,
      net: entry.yearlySaving - (feeNum || 0),
      byCategory: entry.byCategory,
    });
  }
  out.sort((a, b) => b.net - a.net || b.yearlySaving - a.yearlySaving || a.card.localeCompare(b.card));
  return out;
}

/** Default spending profile from the category manifest (defaultMonthlySpend). */
export function defaultMonthlySpend(manifestCategories) {
  const out = {};
  for (const c of manifestCategories || []) {
    const v = Number(c.defaultMonthlySpend);
    if (Number.isFinite(v) && v > 0) out[c.slug] = v;
  }
  return out;
}
